
import { Flight, FlightStatus, City } from "@/types/flight";
import { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { FlightStatusBadge } from "./FlightStatusBadge";
import { SocialShareButtons } from "./SocialShareButtons";
import { CityFlightModal } from "./CityFlightModal";
import { formatTime } from "@/lib/formatters";

interface FlightBoardProps {
  flights: Flight[];
}

export function FlightBoard({ flights }: FlightBoardProps) {
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  console.log('FlightBoard received flights:', flights);
  console.log('FlightBoard flights count:', flights?.length || 0);
  console.log('First flight data:', flights?.[0]);

  const handleCityClick = (city: City) => {
    setSelectedCity(city);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCity(null);
  };

  const sortedFlights = [...flights].sort((a, b) => {
    const dateA = new Date(a.scheduled_departure);
    const dateB = new Date(b.scheduled_departure);
    return dateA.getTime() - dateB.getTime();
  });

  console.log('FlightBoard sortedFlights count:', sortedFlights.length);

  if (!flights || flights.length === 0) {
    console.log('FlightBoard: No flights to display');
    return (
      <div className="w-full p-8 text-center text-foreground">
        <p>No flights available</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-auto">
      <Table>
        <TableHeader className="bg-foreground">
          <TableRow className="hover:bg-foreground">
            <TableHead className="text-background">Flight</TableHead>
            <TableHead className="text-background">From</TableHead>
            <TableHead className="text-background">To</TableHead>
            <TableHead className="text-background">Departure</TableHead>
            <TableHead className="text-background">Arrival</TableHead>
            <TableHead className="text-background">Status</TableHead>
            <TableHead className="text-background">Gate</TableHead>
            <TableHead className="text-background">Aircraft</TableHead>
            <TableHead className="text-background">Share</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedFlights.map((flight, index) => {
            console.log(`Rendering flight ${index}:`, flight.flight_number, flight);
            return (
              <TableRow key={flight.id} className={getRowClass(flight.status)}>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-semibold">{flight.flight_number}</span>
                    <span className="text-xs text-foreground">{flight.organization?.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <button 
                    onClick={() => flight.departure_city && handleCityClick(flight.departure_city)}
                    className="text-left hover:text-primary hover:underline cursor-pointer transition-colors"
                    disabled={!flight.departure_city}
                  >
                    <span>{flight.departure_city?.name}</span>
                    <span className="ml-1 text-foreground">
                      ({flight.departure_city?.code})
                    </span>
                  </button>
                </TableCell>
                <TableCell>
                  <button 
                    onClick={() => flight.arrival_city && handleCityClick(flight.arrival_city)}
                    className="text-left hover:text-primary hover:underline cursor-pointer transition-colors"
                    disabled={!flight.arrival_city}
                  >
                    <span>{flight.arrival_city?.name}</span>
                    <span className="ml-1 text-foreground">
                      ({flight.arrival_city?.code})
                    </span>
                  </button>
                </TableCell>
                <TableCell className="font-medium">
                  {formatTime(flight.scheduled_departure)}
                </TableCell>
                <TableCell className="font-medium">
                  {formatTime(flight.scheduled_arrival)}
                </TableCell>
                <TableCell>
                  <FlightStatusBadge status={flight.status} pulsing={flight.status !== "On Time"} />
                  {flight.status_reason && (
                    <div className="text-xs text-foreground mt-1">{flight.status_reason}</div>
                  )}
                </TableCell>
                <TableCell>{flight.gate || "TBA"}</TableCell>
                <TableCell className="text-sm">{flight.aircraft_type?.code}</TableCell>
                <TableCell>
                  <SocialShareButtons flight={flight} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      <CityFlightModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        city={selectedCity}
      />
    </div>
  );
}

function getRowClass(status: FlightStatus): string {
  // Dynamic row colors based on flight status
  switch (status) {
    case "Delayed":
      return "bg-[#DC2626] text-white hover:bg-muted/50";
    case "Boarding":
      return "bg-[#16A34A] text-white hover:bg-muted/50";
    case "Check-in Open":
      return "bg-[#2563EB] text-white hover:bg-muted/50";
    case "Gate Closed":
      return "bg-[#991B1B] text-white hover:bg-muted/50";
    case "On Time":
      return "bg-[#FFDE21] text-foreground hover:bg-muted/50";
    case "Departed":
      return "bg-[#16A34A] text-white hover:bg-muted/50";
    case "Cancelled":
      return "bg-[#B91C1C] text-white hover:bg-muted/50";
    case "Boarding Soon":
      return "bg-[#F59E0B] text-white hover:bg-muted/50";
    case "In Air":
    case "Landed":
    case "Arrived":
      return "bg-[#16A34A] text-white hover:bg-muted/50";
    default:
      return "hover:bg-muted";
  }
}
